import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, type BadgeTone } from '@artosera/ui';
import { ETATS_JALON } from '@artosera/shared';
import { usePeut } from '../auth/usePeut.js';
import type { EtatJalon, JalonSaisie } from '../types.js';
import pageStyles from '../styles/page.module.css';
import { EtatOnglet, formaterJour, useLangue, useMessageErreur } from './Finances/commun.js';
import { TableauSaisie, type ChampSaisie } from './Finances/TableauSaisie.js';
import { useSaisies } from './Finances/useSaisies.js';

const TON_ETAT: Record<EtatJalon, BadgeTone> = {
  a_venir: 'deposit',
  en_cours: 'accent',
  atteint: 'available',
  abandonne: 'withdrawn',
};

/**
 * Jalons du projet (ARTO-58, direction) : une liste courte, telle que les
 * associés la lisent dans la vue d'aigle. Le libellé sort tel quel : l'aide le
 * rappelle sous le champ.
 */
export function Jalons(): JSX.Element {
  const { t } = useTranslation('console');
  const langue = useLangue();
  const peut = usePeut();
  const message = useMessageErreur('jalons');
  const { lignes, chargement, enErreur, creer, modifier, supprimer } = useSaisies('jalons');

  const champs: ChampSaisie[] = [
    {
      cle: 'libelle',
      titre: t('jalons.champs.libelle'),
      type: 'texte',
      requis: true,
      longueurMax: 160,
      aide: t('saisies.visibleAssocies'),
      pleineLargeur: true,
    },
    { cle: 'echeanceLe', titre: t('jalons.champs.echeance'), type: 'date' },
    {
      cle: 'etat',
      titre: t('jalons.champs.etat'),
      type: 'choix',
      requis: true,
      initiale: 'a_venir',
      options: ETATS_JALON.map((etat) => ({ value: etat, label: t(`jalons.etat.${etat}`) })),
    },
    { cle: 'ordre', titre: t('jalons.champs.ordre'), type: 'entier', videVaut: 0 },
  ];

  return (
    <section>
      <div className={pageStyles.entete}>
        <div>
          <h1>{t('jalons.titre')}</h1>
          <p>{t('jalons.texte')}</p>
        </div>
      </div>

      {chargement || enErreur ? (
        <EtatOnglet chargement={chargement} enErreur={enErreur} />
      ) : (
        <TableauSaisie<JalonSaisie>
          legende={t('jalons.titre')}
          lignes={lignes}
          colonnes={[
            {
              cle: 'ordre',
              titre: t('jalons.colonnes.ordre'),
              numerique: true,
              rendu: (ligne) => ligne.ordre,
            },
            {
              cle: 'libelle',
              titre: t('jalons.colonnes.libelle'),
              rendu: (ligne) => ligne.libelle,
            },
            {
              cle: 'echeance',
              titre: t('jalons.colonnes.echeance'),
              rendu: (ligne) => formaterJour(ligne.echeanceLe, langue, t('jalons.sansEcheance')),
            },
            {
              cle: 'etat',
              titre: t('jalons.colonnes.etat'),
              rendu: (ligne) => (
                <Badge tone={TON_ETAT[ligne.etat]}>{t(`jalons.etat.${ligne.etat}`)}</Badge>
              ),
            },
          ]}
          champs={champs}
          valeursDe={(ligne) => ({
            libelle: ligne.libelle,
            echeanceLe: ligne.echeanceLe,
            etat: ligne.etat,
            ordre: ligne.ordre,
          })}
          libelleLigne={(ligne) => ligne.libelle}
          enEcriture={peut('console:manage-milestones')}
          libelleAjouter={t('jalons.ajouter')}
          messageVide={t('jalons.vide')}
          surCreer={creer}
          surModifier={(ligne, changements) => modifier(ligne.id, changements)}
          surSupprimer={(ligne) => supprimer(ligne.id)}
          message={message}
        />
      )}
    </section>
  );
}
